import type { SeriesMarker, UTCTimestamp } from 'lightweight-charts';
import { barAt } from '../drawings/measure';

export interface DealLike {
  ticket: number;
  /** The position the deal belongs to: an entry and its exits share it. */
  positionId?: number;
  symbol: string;
  type: string;
  entry: string;
  volume: number;
  price: number;
  profit: number;
  commission: number;
  swap: number;
  /** UTC ISO time. */
  time: string;
}

/**
 * Arrows for the account's past deals on this symbol, at the price they filled: an entry shows its side and volume, an exit
 * its result. Bar times are server-time epoch seconds, deal times UTC, hence the offset. Deals before the first loaded bar
 * are left out (they come back when older history is loaded). Sorted by time, as the chart requires.
 */
export function tradeMarkers(
  deals: readonly DealLike[],
  symbol: string,
  bars: readonly { t: number }[],
  offsetMinutes: number,
  colors: { up: string; down: string },
): SeriesMarker<UTCTimestamp>[] {
  const out: (SeriesMarker<UTCTimestamp> & { ticket: number })[] = [];
  for (const d of deals) {
    if (d.symbol !== symbol || (d.type !== 'Buy' && d.type !== 'Sell')) continue;
    const at = Math.floor(Date.parse(d.time) / 1000) + offsetMinutes * 60;
    const bar = barAt(bars, at);
    if (!bar) continue;
    const buy = d.type === 'Buy';
    const result = d.profit + d.commission + d.swap;
    out.push({
      id: `deal-${d.ticket}`,
      ticket: d.ticket,
      time: bar.t as UTCTimestamp,
      position: buy ? 'atPriceBottom' : 'atPriceTop',
      price: d.price,
      shape: buy ? 'arrowUp' : 'arrowDown',
      color: buy ? colors.up : colors.down,
      text:
        d.entry === 'In'
          ? `${d.type} ${d.volume}`
          : `Close ${d.volume} ${result > 0 ? '+' : ''}${result.toFixed(2)}`,
    });
  }
  return out
    .sort((a, b) => a.time - b.time || a.ticket - b.ticket)
    .map(({ ticket: _ticket, ...marker }) => marker);
}
